import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PLAYBOOKS } from './Playbooks';

export default function PlaybookDetail() {
  const { id } = useParams();
  const pb = PLAYBOOKS.find(p => p.id === id);

  if (!pb) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8', gap: 16 }}>
        <div style={{ fontSize: 48 }}>🗺️</div>
        <h2 style={{ color: '#f1f5f9', margin: 0 }}>Playbook not found</h2>
        <Link to="/playbooks" style={{ color: '#38bdf8', textDecoration: 'none' }}>← All Playbooks</Link>
      </div>
    );
  }

  const seoKw = pb.seo?.keywords?.join(', ') || '';
  const emoji = pb.emoji || pb.icon || '📋';

  const s = {
    page: { minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', padding: '32px 16px 80px', fontFamily: 'system-ui, sans-serif' },
    inner: { maxWidth: 760, margin: '0 auto' },
    back: { display: 'inline-flex', alignItems: 'center', gap: 6, color: '#94a3b8', textDecoration: 'none', fontSize: 14, marginBottom: 32 },
    hero: { background: 'linear-gradient(135deg, rgba(56,189,248,.08), rgba(167,139,250,.08))', border: '1px solid rgba(255,255,255,.08)', borderRadius: 20, padding: '36px 32px', marginBottom: 40, textAlign: 'center' },
    emoji: { fontSize: 56, marginBottom: 12 },
    title: { fontSize: 'clamp(22px, 5vw, 34px)', fontWeight: 800, margin: '0 0 8px', background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    subtitle: { fontSize: 16, color: '#94a3b8', margin: 0 },
    meta: { display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' },
    badge: { background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 20, padding: '4px 14px', fontSize: 13, color: '#94a3b8' },
    monthCard: { background: '#1e293b', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, padding: '24px 28px', marginBottom: 20 },
    monthLabel: { fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 },
    monthTitle: { fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 16 },
    step: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, fontSize: 15, color: '#cbd5e1', lineHeight: 1.5 },
    dot: { width: 7, height: 7, borderRadius: '50%', marginTop: 7, flexShrink: 0 },
    cta: { background: 'linear-gradient(135deg, rgba(56,189,248,.1), rgba(167,139,250,.1))', border: '1px solid rgba(56,189,248,.2)', borderRadius: 16, padding: '28px 32px', textAlign: 'center', marginTop: 40 },
    ctaTitle: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 },
    ctaSub: { color: '#94a3b8', fontSize: 15, marginBottom: 20 },
    btns: { display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' },
    btn: { background: 'linear-gradient(135deg, #38bdf8, #a78bfa)', color: '#0f172a', padding: '12px 24px', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none', display: 'inline-block' },
    btnSec: { background: 'rgba(255,255,255,.06)', color: '#f1f5f9', padding: '12px 24px', borderRadius: 10, fontWeight: 600, fontSize: 15, textDecoration: 'none', display: 'inline-block', border: '1px solid rgba(255,255,255,.1)' },
  };

  return (
    <div style={s.page}>
      <Helmet>
        <title>{pb.seo?.title || `${pb.title} | KnowFi Playbooks`}</title>
        <meta name="description" content={pb.seo?.description || pb.subtitle} />
        {seoKw && <meta name="keywords" content={seoKw} />}
        <meta property="og:title" content={pb.seo?.title || pb.title} />
        <meta property="og:description" content={pb.seo?.description || pb.subtitle} />
        <link rel="canonical" href={`https://knowfi.io/playbooks/${pb.id}`} />
      </Helmet>

      <div style={s.inner}>
        <Link to="/playbooks" style={s.back}>← All Playbooks</Link>

        <div style={s.hero}>
          <div style={s.emoji}>{emoji}</div>
          <h1 style={s.title}>{pb.title}</h1>
          <p style={s.subtitle}>{pb.subtitle}</p>
          {(pb.difficulty || pb.timeframe) && (
            <div style={s.meta}>
              {pb.difficulty && <span style={s.badge}>📊 {pb.difficulty}</span>}
              {pb.timeframe && <span style={s.badge}>⏱ {pb.timeframe}</span>}
            </div>
          )}
        </div>

        {pb.months?.map((m, i) => (
          <div key={i} style={s.monthCard}>
            <div style={{ ...s.monthLabel, color: pb.color }}>{m.label}</div>
            <div style={s.monthTitle}>{m.title}</div>
            {m.steps?.map((step, j) => (
              <div key={j} style={s.step}>
                <div style={{ ...s.dot, background: pb.color }} />
                <span>{step}</span>
              </div>
            ))}
          </div>
        ))}

        <div style={s.cta}>
          <div style={s.ctaTitle}>Ready to build your plan?</div>
          <div style={s.ctaSub}>Answer 4 questions and get a personalized money plan built for your situation.</div>
          <div style={s.btns}>
            <Link to="/gameplan" style={s.btn}>🎯 Build My Game Plan</Link>
            <Link to="/playbooks" style={s.btnSec}>← More Playbooks</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
